import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, ShieldCheck, CheckCircle2, Crown, Sparkles, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wordmark } from "@/components/landing/Wordmark";
import { getClaimProvider, submitClaim } from "@/server/claim.functions";
import { authClient } from "@/lib/auth/auth-client";

type Plan = "free" | "premium_monthly" | "premium_yearly";

const PLAN_COPY: Record<Plan, { title: string; price: string; icon: any }> = {
  free: { title: "Free Listing", price: "$0 · always free", icon: CheckCircle2 },
  premium_monthly: { title: "Premium Monthly", price: "$29/month (was $99)", icon: Crown },
  premium_yearly: { title: "Premium Yearly", price: "$99/year (was $999)", icon: Crown },
};

export const Route = createFileRoute("/claim")({
  validateSearch: (s: Record<string, unknown>) => ({
    providerId: typeof s.providerId === "string" ? s.providerId : "",
    plan: (typeof s.plan === "string" ? s.plan : "free") as Plan,
  }),
  head: () => ({ meta: [{ title: "Claim your listing · Heallexa" }, { name: "robots", content: "noindex" }] }),
  component: ClaimPage,
});

function ClaimPage() {
  const { providerId, plan: initialPlan } = useSearch({ from: "/claim" });
  return <ClaimFlow providerId={providerId} initialPlan={initialPlan} />;
}

export function ClaimFlow({ providerId, initialPlan }: { providerId: string; initialPlan: Plan }) {
  const navigate = useNavigate();
  const fetchProvider = useServerFn(getClaimProvider);
  const submit = useServerFn(submitClaim);

  const [step, setStep] = useState<"confirm" | "auth" | "code" | "plan">("confirm");
  const [plan, setPlan] = useState<Plan>(initialPlan ?? "free");
  const [userReady, setUserReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const { data: provider, isLoading } = useQuery({
    queryKey: ["claim-provider", providerId],
    queryFn: () => fetchProvider({ data: { providerId } }),
    enabled: !!providerId,
  });

  useEffect(() => {
    let cancel = false;
    (async () => {
      const { data } = await authClient.getSession();
      if (cancel) return;
      setSignedIn(!!data?.user);
      if (data?.user) setEmail(data.user.email ?? "");
      setUserReady(true);
    })();
    return () => { cancel = true; };
  }, []);

  useEffect(() => {
    if (!provider) return;
    setFullName((prev) => prev || provider.name || "");
    setPhone((prev) => prev || (provider as any).phone || "");
  }, [provider]);

  if (!providerId) {
    return (
      <Shell>
        <div className="p-6 card-surface-flat rounded-md text-center">
          <p className="mb-4">Missing listing ID.</p>
          <Button asChild><Link to="/browse">Browse providers</Link></Button>
        </div>
      </Shell>
    );
  }

  if (isLoading || !provider || !userReady) {
    return <Shell><Center><Loader2 className="h-5 w-5 animate-spin" /></Center></Shell>;
  }

  const runClaim = async () => {
    setBusy(true); setErr(null);
    try {
      const res = await submit({ data: { providerId, plan, userPhone: phone || undefined, userFullName: fullName || undefined } });
      toast.success("Listing claimed — welcome aboard!");
      if (res.needsCheckout) {
        navigate({ to: "/dashboard/new-listing", search: { plan } as any });
      } else {
        navigate({ to: "/provider/dashboard" as any });
      }
    } catch (e: any) {
      setErr(e?.message ?? "Could not complete claim.");
      setStep("plan");
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = () => {
    if (!signedIn) setStep("auth"); else setStep("plan");
  };

  const onAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("heallexa_signup_account_type", "doctor");
        localStorage.setItem("heallexa_claim_provider_id", providerId);
        localStorage.setItem("heallexa_selected_plan", plan);
      }
      // New provider account — Cognito emails a 6-digit code to verify the address.
      const { data, error } = await authClient.signUp.email({ email, password, name: fullName });
      if (!error) {
        if (data?.confirmationRequired) {
          toast.success("We sent a 6-digit code to your email.");
          setStep("code");
        } else {
          setSignedIn(true); setStep("plan");
        }
        return;
      }
      // Email already registered → try signing in with the supplied password.
      const { error: signInError, data: signInData } = await authClient.signIn.email({ email, password });
      if (signInData?.twoFactorRedirect) return; // redirecting to second-factor screen
      if (!signInError) { setSignedIn(true); setStep("plan"); return; }
      setErr("An account already exists for this email. Enter your existing password, or reset it from the login page.");
    } catch (e: any) {
      setErr(e?.message ?? "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  const onVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length < 6) { setErr("Enter the 6-digit code."); return; }
    setBusy(true); setErr(null);
    try {
      // Confirm the code, then auto-sign-in with the password held in form state.
      const { error, data } = await authClient.signUp.confirm({ email, code: otp.trim(), password });
      if (error) { setErr(error.message ?? "Invalid or expired code."); return; }
      if (data?.twoFactorRedirect) return; // redirecting to second-factor screen
      setSignedIn(true);
      setStep("plan");
    } catch (e: any) {
      setErr(e?.message ?? "Could not verify code.");
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setBusy(true); setErr(null);
    try {
      const { error } = await authClient.signUp.resendCode({ email });
      if (error) setErr(error.message);
      else toast.success("New code sent.");
    } finally { setBusy(false); }
  };

  const onGoogle = async () => {
    setBusy(true); setErr(null);
    try {
      if (typeof window !== "undefined") {
        localStorage.setItem("heallexa_signup_account_type", "doctor");
        localStorage.setItem("heallexa_claim_provider_id", providerId);
        localStorage.setItem("heallexa_selected_plan", plan);
      }
      await authClient.signIn.social({
        provider: "google",
        callbackURL: `${window.location.origin}/claim?providerId=${providerId}&plan=${plan}`,
      });
    } catch {
      setErr("Google sign-in did not start. Please try again.");
      setBusy(false);
    }
  };

  const PlanIcon = PLAN_COPY[plan].icon;

  return (
    <Shell>
      <div className="space-y-4">
        <div className="border border-primary/30 bg-primary/5 px-4 py-3 rounded-md flex items-center gap-3">
          <ShieldCheck className="h-5 w-5 text-primary shrink-0" />
          <div className="flex-1">
            <div className="text-[11px] font-semibold tracking-wider uppercase text-primary">Confirming listing</div>
            <div className="text-sm font-semibold">{provider.practice ?? provider.name}</div>
            <div className="text-xs text-muted-foreground">
              {[provider.specialty, [provider.city, provider.state].filter(Boolean).join(", "), provider.npi ? `NPI ${provider.npi}` : null].filter(Boolean).join(" · ")}
            </div>
          </div>
        </div>

        {step === "confirm" && (
          <div className="card-surface rounded-md p-6 space-y-4">
            <h1 className="text-lg font-semibold font-display">Are you the provider, owner, office manager, or authorized representative for this listing?</h1>
            <p className="text-sm text-muted-foreground">Confirming gives you instant access to manage this profile.</p>
            <div className="flex gap-2">
              <Button onClick={onConfirm} className="flex-1">Yes, claim this listing</Button>
              <Button asChild variant="outline"><Link to="/browse">Cancel</Link></Button>
            </div>
          </div>
        )}

        {step === "auth" && (
          <div className="card-surface rounded-md p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
              <Stethoscope className="h-4 w-4 text-primary" /> Quick provider signup
            </div>
            <Button type="button" variant="outline" onClick={onGoogle} disabled={busy} className="w-full gap-2">
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.5 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.4 0-9.9-3.4-11.6-8.2l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C41.4 35.9 44 30.4 44 24c0-1.2-.1-2.4-.4-3.5z"/>
              </svg>
              Continue with Google
            </Button>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
            </div>
            <form onSubmit={onAuth} className="space-y-4">
              <div className="space-y-1.5"><Label>Full name</Label><Input value={fullName} onChange={(e)=>setFullName(e.target.value)} required placeholder="Dr. Jane Smith" /></div>
              <div className="space-y-1.5"><Label>Work email</Label><Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input type="tel" value={phone} onChange={(e)=>setPhone(e.target.value)} placeholder="(555) 123-4567" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={6} /></div>
              <Button type="submit" className="w-full" disabled={busy}>{busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Continue"}</Button>
              {err && <p className="text-sm text-destructive">{err}</p>}
            </form>
          </div>
        )}

        {step === "code" && (
          <form onSubmit={onVerifyCode} className="card-surface rounded-md p-6 space-y-4">
            <div className="flex items-center gap-2 text-xs font-semibold tracking-wider uppercase text-muted-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" /> Verify your email
            </div>
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code we sent to <span className="font-medium text-foreground">{email}</span>.
            </p>
            <div className="space-y-1.5">
              <Label>Verification code</Label>
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                placeholder="123456"
                className="text-center tracking-[0.5em] text-lg"
                required
              />
            </div>
            <Button type="submit" className="w-full" disabled={busy || otp.length < 6}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : "Verify & continue"}
            </Button>
            <div className="flex items-center justify-between text-xs">
              <button type="button" onClick={resendCode} disabled={busy} className="text-primary hover:underline">Resend code</button>
              <button type="button" onClick={() => setStep("auth")} className="text-muted-foreground hover:text-foreground">Change email</button>
            </div>
            {err && <p className="text-sm text-destructive">{err}</p>}
          </form>
        )}

        {step === "plan" && (
          <div className="card-surface rounded-md p-6 space-y-4">
            <div>
              <h2 className="text-lg font-semibold font-display">Choose your plan</h2>
              <p className="text-xs text-muted-foreground">You can upgrade or downgrade later.</p>
            </div>
            <div className="grid gap-2">
              {(Object.keys(PLAN_COPY) as Plan[]).map((p) => {
                const I = PLAN_COPY[p].icon;
                const active = plan === p;
                return (
                  <button key={p} type="button" onClick={()=>setPlan(p)}
                    className={`text-left border ${active?"border-primary bg-primary/5":"border-border bg-background"} rounded-md p-4 flex items-center gap-3`}>
                    <I className={`h-5 w-5 ${active?"text-primary":"text-muted-foreground"}`} />
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{PLAN_COPY[p].title}</div>
                      <div className="text-xs text-muted-foreground">{PLAN_COPY[p].price}</div>
                    </div>
                    {active && <CheckCircle2 className="h-4 w-4 text-primary" />}
                  </button>
                );
              })}
            </div>
            <Button onClick={runClaim} disabled={busy} className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <PlanIcon className="h-4 w-4"/>}
              {plan === "free" ? "Claim & go to dashboard" : "Claim & continue to checkout"}
            </Button>
            {err && <p className="text-sm text-destructive">{err}</p>}
          </div>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-6"><Wordmark className="text-2xl" /></div>
        {children}
        <p className="mt-4 text-center text-xs text-muted-foreground">
          <Link to="/" className="hover:text-foreground">← Back to home</Link>
        </p>
      </div>
    </div>
  );
}

function Center({ children }: { children: React.ReactNode }) {
  return <div className="flex items-center justify-center p-12 text-muted-foreground">{children}</div>;
}
